import React, { useState, useEffect } from 'react';
import styles from './DateRangeInput.module.scss';
import { parse } from 'date-fns';

interface DateInputProps {
  value?: string; // dd/mm/yyyy
  onChange?: (date: string | null) => void;
  placeholder?: string;
  className?: string;
  validationError?: string | null;
  clearError?: () => void;
  id?: string;
  setFocus?: boolean;
}

export const DateInput: React.FC<DateInputProps> = ({
  value = '',
  onChange,
  placeholder = 'dd/mm/yyyy',
  className = '',
  validationError = '',
  clearError,
  id,
  setFocus = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState<string>('');
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (cursorPosition !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      setCursorPosition(null);
    }
  }, [inputValue, cursorPosition]);

  interface DateSection {
    value: string;
    maxLength: number;
  }

  const parseDateSections = (input: string, preserveStructure = false): DateSection[] => {
    const sections: DateSection[] = [
      { value: '', maxLength: 2 }, // day
      { value: '', maxLength: 2 }, // month  
      { value: '', maxLength: 4 }  // year
    ];

    if (preserveStructure && input.includes('/')) {
      // When preserving structure, respect existing slashes to maintain day/month/year boundaries
      let currentSectionIndex = 0;
      for (let i = 0; i < input.length; i++) {
        const char = input[i];
        
        if (char === '/') {
          currentSectionIndex = Math.min(currentSectionIndex + 1, 2);
        } else if (/\d/.test(char) && currentSectionIndex < 3) {
          const section = sections[currentSectionIndex];
          if (section.value.length < section.maxLength) {
            section.value += char;
          }
          // If section is full, try to move to next section
          else if (currentSectionIndex < 2) {
            currentSectionIndex++;
            if (sections[currentSectionIndex].value.length < sections[currentSectionIndex].maxLength) {
              sections[currentSectionIndex].value += char;
            }
          }
        }
      }
    } else {
      // Sequential behavior - extract all digits and distribute in order: DD MM YYYY
      const digits = input.replace(/\D/g, '');
      
      // Day: first 2 digits
      if (digits.length > 0) {
        sections[0].value = digits.slice(0, Math.min(2, digits.length));
      }
      
      // Month: next 2 digits  
      if (digits.length > 2) {
        sections[1].value = digits.slice(2, Math.min(4, digits.length));
      }
      
      // Year: remaining digits (up to 4)
      if (digits.length > 4) {
        sections[2].value = digits.slice(4, Math.min(8, digits.length));
      }
    }

    return sections;
  };

  const buildFormattedDate = (sections: DateSection[]): string => {
    let result = sections[0].value;
    
    if (sections[1].value || sections[2].value) {
      result += '/' + sections[1].value;
      if (sections[2].value) {
        result += '/' + sections[2].value;
      }
    }
    
    return result;
  };

  const calculateCursorPosition = (
    input: string,
    formatted: string,
    originalPos: number
  ): number => {
    // If preserving structure, map position more carefully
    if (input.includes('/')) {
      // Determine cursor position by analyzing character by character
      let targetSection = 0;
      let positionInSection = 0;
      
      for (let i = 0; i < originalPos && i < input.length; i++) {
        const char = input[i];
        if (char === '/') {
          targetSection = Math.min(targetSection + 1, 2);
          positionInSection = 0;
        } else if (/\d/.test(char)) {
          positionInSection++;
        }
      }
      
      // Now find the corresponding position in formatted string
      const formattedSections = parseDateSections(formatted, true);
      let newPos = 0;
      
      // Add up complete previous sections
      for (let i = 0; i < targetSection; i++) {
        newPos += formattedSections[i].value.length;
        if (i < 2 && (formattedSections[i].value || formattedSections[i + 1]?.value)) {
          newPos++; // add slash
        }
      }
      
      // Add position within current section (but don't exceed what's available)
      newPos += Math.min(positionInSection, formattedSections[targetSection].value.length);
      
      return Math.min(newPos, formatted.length);
    }
    
    // Original logic for non-structured input
    let digitsBeforeCursor = 0;
    for (let i = 0; i < Math.min(originalPos, input.length); i++) {
      if (/\d/.test(input[i])) {
        digitsBeforeCursor++;
      }
    }

    let digitCount = 0;
    let newPos = 0;

    for (let i = 0; i < formatted.length; i++) {
      if (/\d/.test(formatted[i])) {
        if (digitCount === digitsBeforeCursor) {
          newPos = i;
          break;
        }
        digitCount++;
        newPos = i + 1;
      } else {
        if (digitCount < digitsBeforeCursor) {
          newPos = i + 1;
        }
      }
    }

    if (digitsBeforeCursor >= digitCount) {
      newPos = formatted.length;
    }

    if (newPos < formatted.length && formatted[newPos] === '/') {
      newPos++;
    }

    return Math.min(newPos, formatted.length);
  };

  const formatInput = (
    input: string,
    currentPos?: number,
  ): { formatted: string; newPos: number } => {
    // Detect if we're editing in the middle of existing structured text
    const hasSlashes = input.includes('/');
    const cursorInMiddle = currentPos !== undefined && currentPos < input.length;
    const isEditingStructured = hasSlashes && cursorInMiddle;
    
    if (isEditingStructured) {
      // Use structure-preserving logic when editing existing formatted dates
      const sections = parseDateSections(input, true);
      const formatted = buildFormattedDate(sections);
      
      let newPos = currentPos || 0;
      if (currentPos !== undefined) {
        newPos = calculateCursorPosition(input, formatted, currentPos);
      }
      
      return { formatted, newPos };
    } else {
      // Use simple sequential logic for fresh input or appending
      const digits = input.replace(/\D/g, '');
      let formatted = '';
      let newPos = currentPos || 0;

      if (digits.length <= 2) {
        formatted = digits;
      } else if (digits.length <= 4) {
        formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
      } else {
        formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
      }
      
      if (currentPos !== undefined) {
        const originalLength = input.length;
        const formattedLength = formatted.length;
        const slashesAdded = formattedLength - originalLength;
        newPos = Math.min(currentPos + slashesAdded, formatted.length);

        // Don't place cursor on slash
        if (formatted[newPos] === '/' && newPos < formatted.length) {
          newPos++;
        }
      }

      return { formatted, newPos };
    }
  };

  const isValidDate = (dateStr: string): boolean => {
    const parsedDate = parse(dateStr, 'dd/MM/yyyy', new Date());

    return !isNaN(parsedDate.getTime());
  };

  const isComplete = (dateStr: string): boolean => {
    return dateStr.length === 10 && dateStr.includes('/');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    const { formatted, newPos } = formatInput(rawValue, cursorPos);

    setInputValue(formatted);
    setCursorPosition(newPos);
    setError('');

    if (value && value.length > 0 && formatted.length === 0) {
      onChange?.(null);
      return;
    }

    if (isComplete(formatted)) {
      if (isValidDate(formatted)) {
        onChange?.(formatted);
      } else {
        setError('Invalid date');
      }
    }
  };
  

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const cursorPos = target.selectionStart || 0;
    const value = target.value;

    if (e.key === 'Backspace') {
      if (cursorPos > 0 && value[cursorPos - 1] === '/') {
        e.preventDefault();
        // Skip over slash when backspacing
        setCursorPosition(cursorPos - 1);
        return;
      }
    }

    if (e.key === 'Delete') {
      if (cursorPos < value.length && value[cursorPos] === '/') {
        e.preventDefault();
        // Skip over slash when deleting
        setCursorPosition(cursorPos + 1);
        return;
      }
    }

    if (e.key === 'ArrowRight') {
      if (cursorPos < value.length && value[cursorPos] === '/') {
        e.preventDefault();
        setCursorPosition(cursorPos + 1);
      }
    }

    if (e.key === 'ArrowLeft') {
      if (cursorPos > 0 && value[cursorPos - 1] === '/') {
        e.preventDefault();
        setCursorPosition(cursorPos - 1);
      }
    }
  };

  const handleBlur = () => {
    // Show error only on blur if input is incomplete or invalid
    if (inputValue.length > 0) {
      if (!isComplete(inputValue)) {
        setError('Date must be complete (dd/mm/yyyy)');
      } else if (!isValidDate(inputValue)) {
        setError('Invalid date');
      }
      clearError?.();
    }
  };

  const handleFocus = () => {
    setError('');
  };

  useEffect(() => {
    if (setFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [setFocus]);

  useEffect(() => {
    try {
      if (
        inputRef.current &&
        inputValue.length === 0 &&
        validationError &&
        validationError.length > 0
      ) {
        inputRef.current.setCustomValidity(validationError || '');
        inputRef.current.reportValidity();
      } else if (inputRef.current) {
        inputRef.current.setCustomValidity('');
        inputRef.current.reportValidity();
      }
    } catch (error) {
      console.error('Error setting custom validity:', error);
    }
  }, [validationError, inputValue, inputRef]);

  const hasError = (validationError && validationError?.length > 0 && inputValue.length === 0) || error;

  return (
    <div className={styles.dateInputContainer}>
      <input
        type="text"
        ref={inputRef}
        onKeyDown={handleKeyDown}
        id={id ? `date-input-${id}` : 'date-input'}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        maxLength={10}
        autoComplete="off"
        className={`${hasError ? styles.error: ''}`}
      />

      {error && <div className={styles.dateInputError}>{error}</div>}
    </div>
  );
};
